"use client";

import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { usePdfSummaries } from "@/hooks/usePdfSummaries";
import { useToolFiles } from "@/hooks/useToolFiles";
import type { Tool } from "@/lib/tools";

export type PagePreview = {
  /** First page size in points, as shown. */
  size: { width: number; height: number };
};

/** Watermark and Page numbers (Phase 5, browser): settings beside a preview of
 *  the first page, then the PDF is stamped in the worker. */
export function StampTool({
  tool,
  button,
  busyLabel,
  ready = true,
  run,
  resultName,
  overlay,
  children,
}: {
  tool: Tool;
  button: string;
  busyLabel: string;
  ready?: boolean;
  run: (file: File) => Promise<Blob>;
  resultName: (file: File) => string;
  /** Drawn over the first page's picture, in a box the page's size. */
  overlay: (page: PagePreview) => React.ReactNode;
  children: React.ReactNode;
}) {
  const { files, setFiles, notices } = useToolFiles(tool);
  const file = files[0];
  const summary = usePdfSummaries(files).get(file);
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function change(next: File[]) {
    setFiles(next);
    setResults(null);
    setError("");
  }

  async function start() {
    setBusy(true);
    setError("");
    try {
      const blob = await run(file);
      const pages = summary && "pages" in summary ? summary.pages : undefined;
      setResults([
        {
          blob,
          name: resultName(file),
          detail: pages ? `${pages} ${pages === 1 ? "page" : "pages"}` : "",
        },
      ]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const broken = summary && "error" in summary ? summary.error : "";

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={change}
      notices={notices}
      results={results}
      onStartOver={() => change([])}
    >
      <section
        aria-label={tool.name}
        className="grid gap-5 rounded-[16px] border bg-card p-4 sm:grid-cols-[minmax(0,220px)_1fr] sm:p-5"
      >
        <figure className="flex flex-col items-center gap-2">
          {summary && "thumb" in summary ? (
            <div
              className="[container-type:inline-size] relative w-full max-w-[220px] overflow-hidden border bg-white shadow-sm"
              style={{
                aspectRatio: `${summary.size.width} / ${summary.size.height}`,
              }}
            >
              <BlobImage
                blob={summary.thumb}
                alt=""
                className="absolute inset-0 size-full"
              />
              {overlay({ size: summary.size })}
            </div>
          ) : (
            <div className="aspect-[3/4] w-full max-w-[220px] animate-pulse rounded bg-sand" />
          )}
          <figcaption className="text-[13px] text-muted-foreground">
            Preview of page 1
          </figcaption>
        </figure>
        <div className="flex flex-col gap-4">
          {children}
          {(broken || error) && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {broken || error}
            </p>
          )}
          {busy ? (
            <ProgressBar label={busyLabel} />
          ) : (
            <button
              type="button"
              onClick={start}
              disabled={!file || !ready || !!broken}
              className={primaryButtonClass}
            >
              {button}
            </button>
          )}
          <p className="text-center text-[13px] text-muted-foreground">
            Nothing is uploaded
          </p>
        </div>
      </section>
    </ToolShell>
  );
}
